import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Loader2, Send, Database, AlertTriangle, FileText,
  Settings as SettingsIcon, Lock, Plus, MessageSquare, Trash2, PanelLeftClose, PanelLeft,
  Coins, Terminal,
} from 'lucide-react';
import insightsService from '../../services/insightsService';
import { useAppContext } from '../../context/AppContext';
import Thread from './Thread';
import { BriefingModal, CatalogueModal, Notice } from './DataPanels';

// =============================================================================
// AI Insights (MOD_11_INSIGHT) — a conversation, not a search box.
//
// Rebuilt from a single-shot Q&A page at Keith's request: "I need it more like
// how you claude code answer and respond… dialogue box at the bottom… I actually
// want to see the AI response like in the program."
//
// THREE THINGS THAT SHAPE THE LAYOUT:
//
//   1. THE COMPOSER IS PINNED AND THE THREAD SCROLLS. A conversation you have to
//      scroll to the bottom of to continue is a conversation people abandon after
//      three turns.
//   2. AN ASSISTANT TURN SHOWS ITS WORKING (Thread.jsx). Steps stream while it
//      runs, then collapse to one line. That is the progress indicator AND the
//      audit trail, and it is why a question taking eight seconds does not feel
//      broken.
//   3. THE FIGURES COME BEFORE THE PROSE. Argued in Thread.jsx — the table is the
//      answer, the paragraph is commentary on it.
//
// FOLLOW-UPS ARE THE POINT. "Which products have the worst margin" then "now by
// site" then "only the 15kg range" is how somebody actually interrogates a
// budget, and each of those is a small edit to the previous query plan rather
// than a fresh question. The server replays prior plans to make that work.
// =============================================================================

export default function Insights() {
  const { selectedVersion } = useAppContext();

  const [models, setModels] = useState([]);
  const [modelsReason, setModelsReason] = useState(null);
  const [selectedModel, setSelectedModel] = useState('');
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);

  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [versionMismatch, setVersionMismatch] = useState(null);

  const [draft, setDraft] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const [showBriefing, setShowBriefing] = useState(false);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  // Offered only to roles holding the direct-SQL module, and only when the
  // server has a read-only database login. Off by default even then: the plan
  // path is a round trip cheaper and auto-scoped, so SQL is for the questions
  // that need it rather than for every question.
  const [sqlAvailable, setSqlAvailable] = useState(false);
  const [sqlMode, setSqlMode] = useState(false);

  const bottomRef = useRef(null);
  const composerRef = useRef(null);

  // -------------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Independent so one failing does not blank the others.
      const [m, c, conv] = await Promise.allSettled([
        insightsService.getModels(),
        insightsService.getCatalogue(),
        insightsService.listConversations(),
      ]);

      if (m.status === 'fulfilled' && m.value?.success) {
        const list = m.value.data.models || [];
        setModels(list);
        setModelsReason(m.value.data.reason || null);
        setSelectedModel((prev) => prev || (list.find((x) => x.is_default) || list[0])?.model_code || '');
        setSqlAvailable(!!m.value.data.sql_mode_available);
      } else {
        setModels([]);
        setModelsReason('The list of available models could not be loaded.');
      }

      if (c.status === 'fulfilled' && c.value?.success) setCatalogue(c.value.data);
      if (conv.status === 'fulfilled' && conv.value?.success) setConversations(conv.value.data.conversations || []);
    } catch (e) {
      setError({ message: e.response?.data?.message || e.message || 'Could not load Insights.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the newest turn in view, the way a terminal does.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: messages.length > 2 ? 'smooth' : 'auto', block: 'end' });
  }, [messages, running]);

  const model = models.find((m) => m.model_code === selectedModel) || null;
  const ready = models.length > 0;
  const sharesResults = !!model?.send_results_to_model;
  const canSend = ready && !!selectedVersion && !running && draft.trim().length > 0;

  // -------------------------------------------------------------------------
  const openConversation = async (id) => {
    setError(null); setRunning(false);
    if (!id) { setConversationId(null); setMessages([]); setVersionMismatch(null); return; }
    setLoading(true);
    try {
      const res = await insightsService.getConversation(id);
      if (res.success) {
        setConversationId(id);
        setMessages(res.data.messages || []);
        setVersionMismatch(res.data.version_mismatch || null);
        if (res.data.conversation?.model_code) setSelectedModel(res.data.conversation.model_code);
      }
    } catch (e) {
      setError({ message: e.response?.data?.message || e.message });
    } finally {
      setLoading(false);
    }
  };

  const removeConversation = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this conversation? The audit record of what was asked is kept.')) return;
    try {
      await insightsService.deleteConversation(id);
      if (id === conversationId) { setConversationId(null); setMessages([]); }
      setConversations((cs) => cs.filter((c) => c.conversation_id !== id));
    } catch (err) {
      setError({ message: err.response?.data?.message || err.message });
    }
  };

  const send = async () => {
    const question = draft.trim();
    if (!question || !selectedModel || running) return;

    setDraft('');
    setError(null);
    setRunning(true);

    // OPTIMISTIC: the question appears immediately and an empty assistant turn
    // sits under it spinning. Waiting several seconds with nothing on screen is
    // what makes a slow feature feel like a broken one.
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: question },
      { role: 'assistant', content: null, steps: [], pending: true },
    ]);

    try {
      // Steps land one at a time and are appended to the pending turn, so the
      // thread shows what is happening rather than a spinner. The final payload
      // is the same shape /ask returns.
      const res = await insightsService.askStream(
        question, selectedModel, conversationId, sqlMode,
        (s) => setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          next[next.length - 1] = { ...last, steps: [...(last.steps || []), s] };
          return next;
        }),
      );

      if (res?.success) {
        const d = res.data;
        setMessages((prev) => {
          const next = [...prev];
          const streamed = next[next.length - 1]?.steps || [];
          next[next.length - 1] = {
            role: 'assistant',
            content: d.answer ?? d.summary ?? d.cannot_answer ?? null,
            // Prefer what the server finally reported, but keep the streamed
            // steps if it sent none — otherwise a completed turn would visibly
            // lose the progress the person just watched.
            steps: (d.steps && d.steps.length) ? d.steps : streamed,
            query: d.query || null,
            outcome: d.outcome,
            results_sent: d.results_sent,
            intent: d.intent,
            columns: d.columns,
            rows: d.rows,
            row_count: d.row_count,
            truncated: d.truncated,
            artifacts: d.artifacts || [],
            usage: d.usage || null,
            from_general_knowledge: !!d.from_general_knowledge,
          };
          return next;
        });

        if (d.conversation_id && d.conversation_id !== conversationId) {
          setConversationId(d.conversation_id);
          // Refresh the list so the new thread appears with its generated title.
          insightsService.listConversations()
            .then((r) => r.success && setConversations(r.data.conversations || []))
            .catch(() => {});
        }
      } else {
        // A failure replaces the pending turn rather than leaving a spinner and
        // an error banner competing to explain the same thing.
        setMessages((prev) => prev.slice(0, -1));
        setError({
          message: res?.message, kind: res?.kind, planErrors: res?.plan_errors,
          retryable: res?.retryable, detail: res?.detail, statusCode: res?.status_code,
        });
      }
    } catch (e) {
      setMessages((prev) => prev.slice(0, -1));
      setError({ message: e.response?.data?.message || e.message || 'The question could not be sent.' });
    } finally {
      setRunning(false);
      composerRef.current?.focus();
    }
  };

  // Enter sends, Shift+Enter is a newline — the convention everywhere else a
  // person types into a conversation. The button stays for discoverability.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // What this conversation has cost so far. A single question is cheap and a
  // long thread is not, and the difference is invisible without a running total —
  // every turn re-sends the catalogue briefing, so turn ten costs about what turn
  // one did.
  const conversationUsage = useMemo(() => {
    const used = messages.map((m) => m.usage).filter(Boolean);
    if (!used.length) return null;
    const priced = used.filter((u) => u.cost != null);
    return {
      total_tokens: used.reduce((n, u) => n + (u.total_tokens || 0), 0),
      cost: priced.length ? priced.reduce((n, u) => n + u.cost, 0) : null,
      currency: priced[0]?.currency || null,
      turns: used.length,
    };
  }, [messages]);

  // Drawn from the catalogue's own subject areas, so they are real questions
  // about THIS tenant's data and never go stale.
  const suggestions = useMemo(() => {
    const out = [];
    for (const d of catalogue?.domains || []) for (const q of d.example_questions || []) out.push(q);
    const step = Math.max(1, Math.floor(out.length / 5));
    return out.filter((_, i) => i % step === 0).slice(0, 5);
  }, [catalogue]);

  const empty = messages.length === 0;

  // -------------------------------------------------------------------------
  return (
    <div className="h-full flex font-sans bg-slate-100 dark:bg-slate-900 overflow-hidden">

      {/* ---------------- CONVERSATION LIST ---------------- */}
      {showSidebar && (
        <aside className="w-60 shrink-0 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col">
          <div className="p-3 border-b border-slate-100 dark:border-slate-700">
            <button
              onClick={() => openConversation(null)}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 dark:bg-slate-600 text-white text-xs font-semibold hover:bg-slate-800"
            >
              <Plus className="w-3.5 h-3.5" /> New conversation
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {conversations.length === 0 ? (
              <p className="m-0 px-2 py-3 text-[11px] text-slate-400">No conversations yet.</p>
            ) : conversations.map((c) => (
              <button
                key={c.conversation_id}
                onClick={() => openConversation(c.conversation_id)}
                className={`group w-full text-left px-2.5 py-2 rounded-lg mb-0.5 ${c.conversation_id === conversationId
                  ? 'bg-slate-100 dark:bg-slate-700'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
              >
                <span className="flex items-start gap-2">
                  <MessageSquare className="w-3 h-3 shrink-0 mt-0.5 text-slate-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] leading-snug text-slate-700 dark:text-slate-200 line-clamp-2">
                      {c.title}
                    </span>
                    <span className="block mt-0.5 text-[10px] text-slate-400">
                      {c.question_count} question{c.question_count === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Remove conversation"
                    onClick={(e) => removeConversation(c.conversation_id, e)}
                    onKeyDown={(e) => e.key === 'Enter' && removeConversation(c.conversation_id, e)}
                    className="opacity-0 group-hover:opacity-100 shrink-0 text-slate-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </span>
                </span>
              </button>
            ))}
          </div>
        </aside>
      )}

      {/* ---------------- MAIN COLUMN ---------------- */}
      <div className="flex-1 min-w-0 flex flex-col">

        {/* Header: identity, what it can see, which model. Stays put. */}
        <header className="shrink-0 px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowSidebar((v) => !v)}
            aria-label={showSidebar ? 'Hide conversations' : 'Show conversations'}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            {showSidebar ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeft className="w-4 h-4" />}
          </button>

          <div className="flex items-center gap-2 mr-auto">
            <Sparkles className="w-4 h-4 text-slate-500" />
            <h1 className="m-0 text-base font-semibold text-slate-900 dark:text-slate-100">AI Insights</h1>
          </div>

          {catalogue?.summary && (
            <button
              onClick={() => setShowCatalogue(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400"
            >
              <Database className="w-3.5 h-3.5" />
              Sees {catalogue.summary.objects} objects
            </button>
          )}
          <button
            onClick={() => setShowBriefing(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400"
          >
            <FileText className="w-3.5 h-3.5" /> Briefing
          </button>

          {conversationUsage && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-slate-400"
              title={`${conversationUsage.turns} turn${conversationUsage.turns === 1 ? '' : 's'} in this conversation`}
            >
              <Coins className="w-3 h-3" />
              {conversationUsage.total_tokens.toLocaleString()}
              {conversationUsage.cost != null && (
                <> · {conversationUsage.currency === 'ZAR' ? 'R' : conversationUsage.currency === 'USD' ? '$' : `${conversationUsage.currency} `}
                  {conversationUsage.cost.toFixed(3)}</>
              )}
            </span>
          )}

          <select
            value={selectedModel}
            disabled={!ready}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-[11px] text-slate-700 dark:text-slate-200 outline-none focus:border-slate-800 disabled:opacity-50 max-w-[230px]"
          >
            {!ready && <option value="">No models configured</option>}
            {models.map((m) => (
              <option key={`${m.provider_code}:${m.model_code}`} value={m.model_code}>
                {m.provider_name} — {m.display_name}
              </option>
            ))}
          </select>
        </header>

        {/* ---------------- THE THREAD ---------------- */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 py-6">
            {loading ? (
              <div className="h-48 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {!ready && (
                  <Notice tone="info" icon={SettingsIcon} title="No model is available to choose.">
                    {modelsReason}{' '}
                    <Link to="/settings" className="underline font-semibold">Open Settings</Link>
                  </Notice>
                )}

                {ready && !selectedVersion && (
                  <Notice tone="warn" icon={AlertTriangle} title="Choose a budget version first.">
                    Almost everything in Mantle belongs to a version. Without one selected in the header, an answer would
                    combine every scenario into one number that looks perfectly reasonable.
                  </Notice>
                )}

                {versionMismatch && (
                  <Notice tone="warn" icon={AlertTriangle} title="This conversation was started in a different budget version.">
                    It began in <strong>{versionMismatch.started_in}</strong> and you are now in{' '}
                    <strong>{versionMismatch.now}</strong>. The figures above have been re-run against the version you
                    have selected now, so they will not match what was originally answered.
                  </Notice>
                )}

                {/* Empty state doubles as the privacy statement and the prompt. */}
                {empty && ready && (
                  <div className="py-10 text-center">
                    <div className="inline-flex w-12 h-12 rounded-xl bg-slate-900 dark:bg-slate-700 text-white items-center justify-center mb-4">
                      <Sparkles className="w-6 h-6" strokeWidth={1.75} />
                    </div>
                    <h2 className="m-0 text-xl font-bold text-slate-900 dark:text-slate-100">
                      Ask a question about the budget
                    </h2>
                    <p className="m-0 mt-1.5 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                      The model is given a catalogue of what your data means, chooses the query, and Mantle runs it.
                      Then keep going — follow-ups build on the answer above.
                    </p>

                    {suggestions.length > 0 && (
                      <div className="mt-6 flex flex-wrap gap-2 justify-center">
                        {suggestions.map((s) => (
                          <button
                            key={s}
                            onClick={() => { setDraft(s.charAt(0).toUpperCase() + s.slice(1) + '?'); composerRef.current?.focus(); }}
                            className="px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-600 text-xs text-slate-600 dark:text-slate-300 hover:border-slate-400 hover:bg-white dark:hover:bg-slate-800"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <Thread
                  messages={messages}
                  running={running}
                  versionId={selectedVersion}
                  modelLabel={model ? (model.display_name || model.model_code) : selectedModel}
                />

                {error && <AskError error={error} />}

                <div ref={bottomRef} />
              </>
            )}
          </div>
        </div>

        {/* ---------------- THE COMPOSER, PINNED ---------------- */}
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="max-w-3xl mx-auto px-6 py-3">
            {/* The privacy posture, stated where the typing happens rather than
                in a banner somebody scrolled past four turns ago. */}
            {ready && (
              <p className={`m-0 mb-2 inline-flex items-center gap-1.5 text-[10px] ${sharesResults
                ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {sharesResults ? <AlertTriangle className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {sharesResults
                  ? `${model.provider_name} sees the figures your questions return.`
                  : 'Figures never leave Mantle — the model only chooses the query.'}
              </p>
            )}

            {/* DIRECT SQL. Shown only where it is actually available, and it says
                what it changes rather than just naming itself — the thing that
                differs is not "more powerful", it is that Mantle stops adding
                the version, site and GL filters. */}
            {sqlAvailable && (
              <label className="mb-2 inline-flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={sqlMode}
                  onChange={(e) => setSqlMode(e.target.checked)}
                />
                <span className="text-[10px]">
                  <span className={`inline-flex items-center gap-1 font-medium ${sqlMode
                    ? 'text-violet-600 dark:text-violet-400' : 'text-slate-500 dark:text-slate-400'}`}>
                    <Terminal className="w-3 h-3" /> Direct SQL
                  </span>
                  <span className="block text-slate-400">
                    {sqlMode
                      ? 'The model writes the SQL. Needed for margin calculations and version comparisons — but Mantle does not add the version, site or GL filters it normally adds, so check what the answer covers.'
                      : 'Let the model write SQL instead of a checked query plan. Slower and unscoped, but it can do arithmetic and compare versions.'}
                  </span>
                </span>
              </label>
            )}

            <div className="flex items-end gap-2">
              <textarea
                ref={composerRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                rows={Math.min(6, Math.max(1, draft.split('\n').length))}
                maxLength={4000}
                disabled={!ready || !selectedVersion}
                placeholder={
                  !ready ? 'Configure an AI provider in Settings first'
                    : !selectedVersion ? 'Choose a budget version in the header first'
                      : messages.length ? 'Ask a follow-up…'
                        : 'Ask a question about the budget…'
                }
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100 outline-none focus:border-slate-800 dark:focus:border-slate-400 resize-none disabled:opacity-50"
              />
              <button
                onClick={send}
                disabled={!canSend}
                aria-label="Send"
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 dark:bg-slate-600 text-white hover:bg-slate-800 disabled:opacity-30"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>

            <p className="m-0 mt-1.5 text-[10px] text-slate-400">
              Enter to send, Shift+Enter for a new line. Ask anything — questions that need your data are answered from it,
              read-only and scoped to your role; anything else is answered directly and labelled as such.
            </p>
          </div>
        </div>
      </div>

      {showBriefing && <BriefingModal modelCode={selectedModel} onClose={() => setShowBriefing(false)} />}
      {showCatalogue && catalogue && <CatalogueModal data={catalogue} onClose={() => setShowCatalogue(false)} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Failures. Each kind gets a different next step, because "try again" and "go and
// fix the API key" are not the same advice.
// -----------------------------------------------------------------------------
const AskError = ({ error }) => {
  const admin = ['auth', 'network', 'bad_request'].includes(error.kind);
  return (
    <Notice tone="danger" icon={AlertTriangle} title={
      error.kind === 'timeout' ? 'That took too long.'
        : error.kind === 'rate_limit' ? 'The AI service is busy.'
          : error.kind === 'auth' ? 'The AI service rejected the API key.'
            : 'That question could not be answered.'
    }>
      {error.message}
      {error.retryable && ' Try again in a moment.'}
      {admin && <> <Link to="/settings" className="underline font-semibold">Open Settings</Link></>}

      {error.detail && (
        <details className="mt-2">
          <summary className="cursor-pointer font-semibold">
            What the AI service said{error.statusCode ? ` (HTTP ${error.statusCode})` : ''}
          </summary>
          <pre className="m-0 mt-1 p-2 rounded bg-slate-900/80 text-slate-100 text-[10px] overflow-x-auto whitespace-pre-wrap">
            {error.detail}
          </pre>
        </details>
      )}

      {error.planErrors?.length > 0 && (
        <>
          <br /><br />What the model got wrong:
          <ul className="m-0 mt-1 pl-4 list-disc">
            {error.planErrors.slice(0, 4).map((e, i) => <li key={i}>{e}</li>)}
          </ul>
          Rephrasing usually fixes it. If it keeps happening, the catalogue may be missing something.
        </>
      )}
    </Notice>
  );
};
